import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MessageSquare,
  Users,
  ShieldCheck,
  CheckCircle2,
  Clock,
  BarChart,
  Zap,
  Send
} from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';

export const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#111A4A] font-sans antialiased overflow-x-hidden">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden bg-[#F8FAFC]">
        {/* Right Edge-to-Edge Image with Fade */}
        <div className="absolute top-0 right-0 w-full lg:w-[60%] h-full">
          <div className="absolute inset-0 bg-cover bg-left" style={{ backgroundImage: "url('/landing-image-2.png')" }}></div>

          {/* Floating Card right */}
          <div className="absolute top-20 right-4 lg:right-16 bg-white shadow-xl rounded-xl p-4 flex items-center gap-3 z-10 w-max border border-white/50">
            <div className="bg-[#E0E7FF] p-2 rounded-full text-[#6046FF] shrink-0">
              <Send className="w-4 h-4" />
            </div>
            <div className="flex flex-col pr-4">
              <span className="font-extrabold text-[12px] text-[#111A4A]">Stay Connected</span>
              <span className="text-[10px] text-gray-500 font-medium">From Anywhere</span>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-6 lg:px-12">
          {/* Left Text */}
          <div className="flex flex-col justify-center text-left max-w-[500px] lg:-ml-8">
            <div className="inline-block w-fit bg-[#E0E7FF] text-[#6046FF] font-bold px-3 py-1.5 rounded-full text-[9px] tracking-wider mb-6 uppercase shadow-sm">
              All-in-one Team Communication
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-[46px] font-black text-[#111A4A] leading-[1.1] mb-5 tracking-tight">
              Connect easily<br />
              Communicate clearly<br />
              <span className="text-[#6046FF]">Work better together</span>
            </h1>
            <p className="text-sm md:text-[15px] text-gray-600 mb-8 font-medium leading-relaxed max-w-[440px]">
              Connect with your team, communicate effortlessly, and keep everyone in sync — all from one simple workspace.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Link to={isAuthenticated ? "/app" : "/register"} className="bg-[#6046FF] hover:bg-[#4338CA] text-white px-7 py-3 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg shadow-[#6046FF]/20 transition-transform hover:scale-105">
                Get Started for Free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="bg-white text-[#111A4A] border border-gray-200 hover:border-gray-300 px-7 py-3 rounded-full text-sm font-bold shadow-sm transition-colors">
                Find your subscription
              </Link>
            </div>

            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-[10px] bg-[#6046FF] text-white flex items-center justify-center shrink-0">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <div className="text-[11px] font-extrabold text-[#111A4A] leading-tight">Team<br />Messaging</div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-[10px] bg-[#3a9efd] text-white flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4" />
                </div>
                <div className="text-[11px] font-extrabold text-[#111A4A] leading-tight">Easy<br />Collaboration</div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-[10px] bg-[#6046FF] text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="text-[11px] font-extrabold text-[#111A4A] leading-tight">Secure &<br />Reliable</div>
              </div>
            </div>
          </div>
          {/* Center Column (Mockup) */}
          <div className="relative w-full lg:w-[550px] z-20 hidden lg:block scale-[0.75] origin-left lg:-translate-x-32 lg:translate-y-36">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 flex h-[480px]">
              {/* Mockup Sidebar */}
              <div className="w-16 bg-[#111A4A] h-full flex flex-col items-center py-4 gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs shadow-md">SC</div>
                <div className="w-8 h-8 rounded-full bg-white/20"></div>
                <div className="w-8 h-8 rounded-full bg-white/20"></div>
                <div className="mt-auto w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Users className="w-4 h-4 text-white/50" /></div>
              </div>
              {/* Mockup Channels */}
              <div className="w-48 border-r border-gray-100 bg-gray-50 p-4">
                <div className="text-[10px] font-bold mb-4 tracking-wider text-gray-400 uppercase">Sahaj Workspace</div>
                <div className="text-[10px] font-bold text-gray-400 mb-2 uppercase">Channels</div>
                <div className="text-xs bg-blue-100 text-blue-700 rounded p-1.5 mb-1 font-bold flex justify-between items-center">
                  <span># general</span>
                  <span className="bg-blue-600 text-white text-[8px] px-1 rounded">2 new</span>
                </div>
                <div className="text-xs p-1.5 mb-1 font-medium text-gray-600 hover:bg-gray-100 rounded cursor-pointer"># project-updates</div>
                <div className="text-xs p-1.5 mb-1 font-medium text-gray-600 hover:bg-gray-100 rounded cursor-pointer"># engineering</div>
                <div className="text-xs p-1.5 mb-1 font-medium text-gray-600 hover:bg-gray-100 rounded cursor-pointer"># design</div>
                <div className="text-[10px] font-bold text-gray-400 mt-4 mb-2 uppercase">Direct Messages</div>
                <div className="text-xs p-1.5 mb-1 font-medium text-gray-600 flex items-center gap-2"><div className="w-4 h-4 bg-purple-200 rounded-full"></div> Karthik</div>
                <div className="text-xs p-1.5 mb-1 font-medium text-gray-600 flex items-center gap-2"><div className="w-4 h-4 bg-orange-200 rounded-full"></div> Aarti</div>
              </div>
              {/* Mockup Main */}
              <div className="flex-1 p-4 flex flex-col bg-white relative">
                <div className="border-b border-gray-100 pb-3 mb-4 flex items-center justify-between">
                  <div className="font-bold text-sm"># general <span className="text-[10px] bg-orange-100 text-orange-600 px-1 py-0.5 rounded ml-2 font-bold uppercase">Private</span></div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"><Users className="w-3 h-3" /> 24</div>
                </div>
                <div className="flex-1 flex flex-col gap-5 overflow-hidden">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#6046FF] text-white flex items-center justify-center shadow font-bold text-xs">B</div>
                    <div className="flex-1">
                      <div className="text-xs font-bold flex items-center gap-2">SahajBot <span className="bg-[#E0E7FF] text-[#6046FF] text-[9px] px-1 rounded font-bold">BOT</span> <span className="text-gray-400 font-normal">10:24 AM</span></div>
                      <div className="text-[13px] text-gray-700 mt-1 bg-gray-50 p-2 rounded-lg rounded-tl-none border border-gray-100 inline-block leading-relaxed">Here's a quick update on today's discussion and next steps. Let's keep things moving forward! 🚀</div>
                      <div className="flex gap-1 mt-2">
                        <div className="text-[10px] bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">👍 12</div>
                        <div className="text-[10px] bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">🔥 4</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-100 overflow-hidden shrink-0">
                      <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: "url('/landing-image-4.png')" }}></div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-bold flex items-center gap-2">Aarti Kulkarni <span className="text-gray-400 font-normal">11:20 AM</span></div>
                      <div className="text-[13px] text-gray-700 mt-1 inline-block leading-relaxed">Great progress! The new workflow looks solid. Let's continue the discussion in the next sync.</div>
                      <div className="flex gap-1 mt-2">
                        <div className="text-[10px] bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-full flex items-center gap-1">👏 6</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Message Input Mock */}
                <div className="absolute bottom-4 left-4 right-4 bg-gray-50 border border-gray-200 rounded-lg p-2.5 flex items-center justify-between">
                  <div className="text-[11px] text-gray-400 pl-2">Message #general...</div>
                  <div className="bg-[#6046FF] p-1.5 rounded text-white shadow hover:scale-105 transition-transform"><ArrowRight className="w-3 h-3" /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED LOGOS */}
      <section className="py-8 border-y border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto text-center px-4">
          <p className="text-[10px] font-bold text-[#111A4A]/60 tracking-widest mb-6 uppercase">Trusted by innovative teams worldwide</p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 items-center text-[#111A4A] font-bold text-sm">
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-[#6046FF] rounded-sm flex items-center justify-center text-white text-[8px]">A</div>ACME GLOBAL</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded-sm"></div>VERTEX SYSTEMS</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-cyan-500 rounded-sm"></div>NEXUS CLOUD</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-[#6046FF] rounded-sm"></div>HYPERSCALE</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-400 rounded-sm"></div>AURA LABS</div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-orange-500 rounded-sm"></div>VANGUARD AI</div>
          </div>
        </div>
      </section>

      {/* AI SECTION */}
      <section className="relative py-24 bg-[#F8FAFC] overflow-hidden">
        {/* Left Edge-to-Edge Image with Fade */}
        <div className="absolute top-0 left-0 w-full lg:w-1/2 h-full">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/landing-image-3.png')" }}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#F8FAFC]"></div>
        </div>

        <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center px-6">

          {/* Left Column - Floating SahajBot Menu */}
          <div className="flex justify-end lg:pr-12 -mt-10 lg:-mt-24 relative z-20">
            <div className="bg-white/95 backdrop-blur-md shadow-2xl rounded-2xl p-4 w-64 border border-white/50">
              <div className="font-extrabold text-[14px] mb-3 flex items-center gap-2 text-[#111A4A]">
                SahajBot
                <div className="w-4 h-4 bg-[#6046FF] rounded-full text-white flex items-center justify-center text-[8px]">✨</div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 bg-[#F0F5FF] p-2.5 rounded-xl border border-blue-100">
                  <span className="text-blue-500 text-base shrink-0">✨</span>
                  <div className="leading-tight">
                    <div className="font-bold text-[11px] text-[#111A4A]">Summarize thread</div>
                    <div className="text-[9px] text-gray-500 font-medium mt-0.5">Get key points instantly</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#FFF8F0] p-2.5 rounded-xl border border-orange-100">
                  <span className="text-orange-500 text-base shrink-0">✍️</span>
                  <div className="leading-tight">
                    <div className="font-bold text-[11px] text-[#111A4A]">Draft a response</div>
                    <div className="text-[9px] text-gray-500 font-medium mt-0.5">Write faster with AI</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#F0FDF4] p-2.5 rounded-xl border border-green-100">
                  <span className="text-green-500 text-base shrink-0">🔍</span>
                  <div className="leading-tight">
                    <div className="font-bold text-[11px] text-[#111A4A]">Find information</div>
                    <div className="text-[9px] text-gray-500 font-medium mt-0.5">Search across all channels</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-[#FFF0F5] p-2.5 rounded-xl border border-pink-100">
                  <span className="text-pink-500 text-base shrink-0">⚡</span>
                  <div className="leading-tight">
                    <div className="font-bold text-[11px] text-[#111A4A]">Generate action items</div>
                    <div className="text-[9px] text-gray-500 font-medium mt-0.5">Turn ideas into tasks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Text & Extra Mockups */}
          <div className="flex flex-col justify-center text-left relative z-20 pb-20">
            <div className="inline-block w-fit bg-[#E0E7FF] text-[#6046FF] font-bold px-3 py-1 rounded-full text-[10px] tracking-wide mb-5 uppercase shadow-sm">
              AI Intelligence
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-[#111A4A] leading-[1.1] mb-5 tracking-tight">
              Intelligence at the core.
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-8 font-medium leading-relaxed max-w-[420px]">
              SahajBot isn't just an add-on; it's woven into the fabric of your workspace. Get instant summaries, draft responses, and find information across all your channels without leaving the conversation.
            </p>
            <div className="flex flex-col gap-4 text-left w-full text-[13px] font-extrabold text-[#111A4A]">
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Context-aware AI assistance</div>
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Automated thread summaries</div>
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Smart search across all apps</div>
            </div>

            {/* Extra Floating Mockups */}

            <div className="absolute right-0 bottom-0 translate-x-0 translate-y-12 hidden xl:block z-30">
              <div className="bg-white shadow-2xl rounded-2xl p-4 w-64 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-end gap-0.5 text-[#6046FF] shrink-0">
                    <div className="w-1.5 h-3 bg-[#6046FF] rounded-t-sm"></div>
                    <div className="w-1.5 h-5 bg-[#6046FF] rounded-t-sm"></div>
                    <div className="w-1.5 h-4 bg-[#6046FF] rounded-t-sm"></div>
                  </div>
                  <div>
                    <div className="font-extrabold text-[13px] text-[#111A4A]">Key Takeaways</div>
                    <div className="text-[9px] text-gray-500 font-medium">Auto-generated from this thread</div>
                  </div>
                </div>
                <ul className="space-y-2 text-[11px] text-[#111A4A] font-bold">
                  <li className="flex items-center gap-2"><div className="bg-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3 h-3 text-white" /></div> Project requirements aligned</li>
                  <li className="flex items-center gap-2"><div className="bg-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3 h-3 text-white" /></div> Design review scheduled</li>
                  <li className="flex items-center gap-2"><div className="bg-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3 h-3 text-white" /></div> Action items created</li>
                </ul>
              </div>
            </div>

            <div className="absolute right-0 top-48 hidden xl:flex flex-row gap-10 z-20 items-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-white rounded-full shadow-xl border border-gray-100 flex items-center justify-center text-[#3a9efd]">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                </div>
                <span className="text-[11px] font-extrabold text-[#111A4A]">Get Insights</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-white rounded-full shadow-xl border border-gray-100 flex items-center justify-center text-[#6046FF]">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                </div>
                <span className="text-[11px] font-extrabold text-[#111A4A]">Create Tasks</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COLLABORATION SECTION */}
      <section className="relative py-24 bg-white overflow-hidden">
        {/* Right Edge-to-Edge Image with Fade */}
        <div className="absolute top-0 right-0 w-full lg:w-[65%] h-full">
          <div className="absolute inset-0 bg-cover bg-left" style={{ backgroundImage: "url('/landing-image-4.png')" }}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/60 to-transparent"></div>
        </div>

        <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center px-6">

          {/* Left Text */}
          <div className="flex flex-col justify-center text-left relative z-20 pt-10 pb-20 lg:py-0">
            <div className="inline-block w-fit bg-[#E0E7FF] text-[#6046FF] font-bold px-3 py-1 rounded-full text-[10px] tracking-wide mb-5 uppercase shadow-sm">
              Seamless Collaboration
            </div>
            <h2 className="text-3xl lg:text-[40px] font-black text-[#111A4A] leading-[1.1] mb-5 tracking-tight whitespace-nowrap">
              Work together in realtime
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-8 font-medium leading-relaxed max-w-[420px]">
              Connect instantly with your team, share files, and move projects forward without friction. Whether you're in the office or remote, SahajConnect keeps everyone perfectly aligned and engaged.
            </p>
            <div className="flex flex-col gap-4 text-left w-full text-[13px] font-extrabold text-[#111A4A]">
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Instant team messaging</div>
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Seamless file sharing</div>
              <div className="flex items-center gap-3"><div className="bg-transparent border-2 border-[#6046FF] rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#6046FF]" /></div> Integrated video calls</div>
            </div>
          </div>

          {/* Right Mockups */}
          <div className="relative h-[450px] lg:h-[550px] flex items-center justify-center">

            {/* Top Left Card - Team Workspace */}
            <div className="absolute left-[-60px] top-8 hidden lg:block bg-white/95 backdrop-blur shadow-2xl rounded-2xl p-4 w-64 border border-white/50 z-20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#F3E8FF] text-[#6046FF] flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-extrabold text-[13px] text-[#111A4A]">Team Workspace</div>
                  <div className="text-[10px] text-gray-500 font-medium">Everyone stays aligned</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-1 mt-1">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-3.png')" }}></div></div>
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-4.png')" }}></div></div>
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-3.png')" }}></div></div>
                </div>
                <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-[9px] font-bold flex items-center justify-center border-2 border-white">+3</div>
              </div>
            </div>

            {/* Bottom Left Card - Live Meeting (Moved below Team Workspace) */}
            <div className="absolute left-[-20px] top-40 hidden lg:block bg-white/95 backdrop-blur shadow-2xl rounded-2xl p-4 w-64 border border-white/50 z-20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-500 flex items-center justify-center shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
                </div>
                <div>
                  <div className="font-extrabold text-[13px] text-[#111A4A]">Live Meeting</div>
                  <div className="text-[10px] text-gray-500 font-medium">4 teammates online</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-1 mt-1">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-3.png')" }}></div></div>
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-4.png')" }}></div></div>
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden relative"><div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-3.png')" }}></div></div>
                  <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden relative">
                    <div className="w-full h-full bg-cover" style={{ backgroundImage: "url('/landing-image-4.png')" }}></div>
                    <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 border-2 border-white rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* STATS BANNER */}
      <section className="bg-gradient-to-r from-[#EEF2FF] via-[#F3E8FF] to-[#EEF2FF] py-14 px-6 border-y border-gray-100">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-[#111A4A]">
          <div className="flex items-center justify-center gap-5">
            <Clock className="w-12 h-12 text-[#3b82f6]" strokeWidth={1.5} />
            <div className="text-left">
              <div className="text-4xl font-black text-[#111A4A] leading-none mb-1">97</div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Minutes Saved Weekly</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-5 border-y md:border-y-0 md:border-l md:border-r border-blue-200/40 py-6 md:py-0">
            <BarChart className="w-12 h-12 text-[#6046FF]" strokeWidth={1.5} />
            <div className="text-left">
              <div className="text-4xl font-black text-[#111A4A] leading-none mb-1">35%</div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Increase in Efficiency</div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-5">
            <Zap className="w-12 h-12 text-[#3b82f6]" strokeWidth={1.5} />
            <div className="text-left">
              <div className="text-4xl font-black text-[#111A4A] leading-none mb-1">10x</div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Faster Onboarding</div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 px-6 text-center bg-[#292a73] relative overflow-hidden border-t border-gray-100">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1b4b] to-[#292a73] pointer-events-none" />
        <div className="max-w-3xl mx-auto space-y-6 relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-[42px] font-black text-white leading-[1.15] tracking-tight">
            See all that you can accomplish<br />in SahajConnect.
          </h2>
          <p className="text-sm md:text-base text-gray-200 font-medium mb-10 max-w-xl mx-auto">
            Join thousands of forward-thinking teams building the future of work.
          </p>
          <div className="pt-2">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-[#f5a524] hover:bg-[#d98b00] text-[#111A4A] px-8 py-4 rounded-full text-sm font-bold shadow-xl transition-all hover:scale-105 hover:-translate-y-1"
            >
              Get Started Today for Free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
